import "@src/deprecated/pages/PatientLayout/components/AIScribe/AIScribe.init";

import {
  Check,
  FileText,
  Mic,
  Play,
  Settings,
  Sparkles,
  Square,
} from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";

import {
  AudioInputDeviceSelect,
  isFinishedStep,
  isIdleStep,
  isPausedStep,
  isRecordingActiveStep,
  type SaveTranscriptItem,
  type UseScribeOptions,
  useScribe,
  useScribeReady,
  useStep,
} from "@elation/transcribe";

import { useIsInElationEHR } from "@src/features/iframe";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";
import { Spinner } from "@src/shared/components/ui/spinner";
import { cn } from "@src/shared/lib/utils";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";
import { datadogLogs } from "@src/shared/utils/observability/datadog";

import { ScribeLanguageSelect } from "./components/ScribeLanguageSelect";
import { Transcript } from "./components/Transcript";

const DEFAULT_WEBSOCKET_URL = "ws://localhost:8080/ws/transcribe";

const stubSaveTranscriptItem: SaveTranscriptItem = async () => {
  // no-op default used when the parent does not provide a saver
};

/**
 * Reports a failed scribe action: logs the underlying error to Datadog (RUM
 * has `forwardErrorsToLogs: false`, so swallowed errors are otherwise
 * invisible to engineering) and shows the user the server's actionable
 * message when one is present, falling back to generic copy otherwise.
 */
function reportVisitActionError(
  errorId: string,
  error: unknown,
  fallbackMessage: string,
  context?: Record<string, unknown>,
) {
  datadogLogs.logger.error(
    errorId,
    { errorId, ...context },
    error instanceof Error ? error : undefined,
  );
  toast.error(getServerErrorMessage(error) ?? fallbackMessage);
}

export type AIScribeProps = UseScribeOptions & {
  isActiveEncounter?: boolean;
  encounterCompleted: boolean;
  encounterFinalizing: boolean;
  hasSoapnote?: boolean;
  onFinishVisit: () => Promise<void>;
  onStartVisit: () => Promise<string>;
  lastOffset: number;
  encounterId: number | null;
};

export function AIScribe(props: AIScribeProps) {
  const encounterCompleted = props.encounterCompleted ?? false;
  const hasSoapnote = props.hasSoapnote ?? false;
  const {
    getScribingSessionId,
    lastOffset,
    onFinishVisit,
    onStartVisit,
    saveTranscriptItem,
    websocketUrl,
  } = props;

  const scribeOptions = useMemo<UseScribeOptions>(
    () => ({
      websocketUrl: websocketUrl ?? DEFAULT_WEBSOCKET_URL,
      getScribingSessionId,
      saveTranscriptItem: saveTranscriptItem ?? stubSaveTranscriptItem,
      lastOffset,
    }),
    [getScribingSessionId, lastOffset, saveTranscriptItem, websocketUrl],
  );

  const isInElationEHR = useIsInElationEHR();

  const { startRecording, stopRecording } = useScribe(scribeOptions);

  const isScribeReady = useScribeReady();
  const [step] = useStep();

  const startDisabled = !isScribeReady || encounterCompleted;
  const scribeLanguageDisabled =
    isRecordingActiveStep(step) || step === "finishing";

  return (
    <HorizontalFlexbox
      alignItems="center"
      gap="xs"
      className="shrink-0"
      data-step={step}
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Transcript"
            className="size-8 sm-stitch:size-9"
          >
            <FileText />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          className="w-[min(calc(100vw-2rem),28rem)] p-3"
          aria-label="Transcript"
        >
          <Transcript encounterId={props.encounterId} />
        </PopoverContent>
      </Popover>

      {hasSoapnote ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="gap-1.5 px-3"
          disabled
          aria-label="Visit completed"
        >
          <Check className="size-4" />
          <span className="hidden sm-stitch:inline">Visit Completed</span>
        </Button>
      ) : (
        <>
          {step === "finishing" ? <Spinner size="lg" /> : null}

          {isIdleStep(step) &&
          !isPausedStep(step) &&
          !isFinishedStep(step) &&
          !isInElationEHR ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5 px-3"
              disabled={startDisabled}
              aria-label={
                step === "finished" ? "Continue visit" : "Start visit"
              }
              onClick={async () => {
                try {
                  const authToken = await onStartVisit();
                  startRecording(authToken);
                } catch (error) {
                  reportVisitActionError(
                    "ai_scribe.start_visit_failed",
                    error,
                    "Couldn't start the visit. Please try again.",
                    { encounterId: props.encounterId },
                  );
                }
              }}
            >
              <Mic className="size-4" />
              <span className="hidden sm-stitch:inline">
                {step === "finished" ? "Continue Visit" : "Start Visit"}
              </span>
            </Button>
          ) : null}

          {isRecordingActiveStep(step) ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className={cn("gap-1.5 px-3 shadow-sm")}
              aria-label="Stop recording"
              onClick={() => {
                stopRecording();
              }}
            >
              <Square className="size-4 fill-current" />
              <span className="hidden sm-stitch:inline">Stop</span>
            </Button>
          ) : null}

          {isPausedStep(step) ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5 px-3"
              aria-label="Resume recording"
              onClick={async () => {
                try {
                  const scribingSessionId = await props.getScribingSessionId();
                  startRecording(scribingSessionId);
                } catch (error) {
                  reportVisitActionError(
                    "ai_scribe.resume_visit_failed",
                    error,
                    "Couldn't resume the visit. Please try again.",
                    { encounterId: props.encounterId },
                  );
                }
              }}
            >
              <Play className="size-4" />
              <span className="hidden sm-stitch:inline">Resume</span>
            </Button>
          ) : null}

          {isFinishedStep(step) && onFinishVisit ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5 bg-emerald-600 px-3 text-white hover:bg-emerald-700"
              aria-label="Finish visit"
              onClick={async () => {
                try {
                  await onFinishVisit();
                } catch (error) {
                  reportVisitActionError(
                    "ai_scribe.finish_visit_failed",
                    error,
                    "Couldn't finish the visit. Please try again.",
                    { encounterId: props.encounterId },
                  );
                }
              }}
            >
              <Sparkles className="size-4" />
              <span className="hidden sm-stitch:inline">Finish Visit</span>
            </Button>
          ) : null}
        </>
      )}

      {!isInElationEHR && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Scribe settings"
              className="size-8 sm-stitch:size-9"
            >
              <Settings />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            className="w-[min(calc(100vw-2rem),22rem)]"
            aria-labelledby="audio-settings-heading"
          >
            <VerticalFlexbox gap="sm">
              <HeaderText
                tag="h3"
                fontWeight={600}
                className="text-sm"
                id="audio-settings-heading"
              >
                Audio Settings
              </HeaderText>
              <VerticalFlexbox gap="xs">
                <HeaderText tag="h4" fontWeight={600} className="text-sm">
                  Transcription Settings
                </HeaderText>
                <ScribeLanguageSelect disabled={scribeLanguageDisabled} />
              </VerticalFlexbox>
              <VerticalFlexbox gap="xs">
                <HeaderText tag="h4" fontWeight={600} className="text-sm">
                  Audio Settings
                </HeaderText>
                <AudioInputDeviceSelect />
              </VerticalFlexbox>
            </VerticalFlexbox>
          </PopoverContent>
        </Popover>
      )}
    </HorizontalFlexbox>
  );
}
